import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useDateLocale } from '../../../utils/dateLocale';
import { catHex } from './meta';
import type { Expense } from '../../../types';

const GAP      = 6;    // px between bars
const H_MAX    = 96;   // bar area height px
const LABEL_H  = 22;   // day label area px
const SVG_H    = H_MAX + LABEL_H;

interface DayData {
  date: string;
  total: number;
  color: string;
  barX: number;
}

interface Props {
  expenses: Expense[];
  currency: string;
  /** The day the ledger is filtered to, so the chart shows which one it is. */
  selectedDay?: string | null;
  /** Clicking a bar is the shortcut to the same filter the pills drive. */
  onSelectDay?: (day: string) => void;
}

export function SpendingByDay({ expenses, currency, selectedDay = null, onSelectDay }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [hovered, setHovered] = useState<DayData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(300);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const byDay = expenses.reduce((acc, e) => {
    const day = e.date.slice(0, 10);
    if (!acc[day]) acc[day] = { total: 0, cats: {} as Record<string, number> };
    acc[day].total += Number(e.amount);
    acc[day].cats[e.category] = (acc[day].cats[e.category] ?? 0) + Number(e.amount);
    return acc;
  }, {} as Record<string, { total: number; cats: Record<string, number> }>);

  const sorted = Object.entries(byDay).sort(([a], [b]) => a.localeCompare(b));
  const n = sorted.length;
  const barW = n > 1 ? (containerW - (n - 1) * GAP) / n : containerW;

  const days: DayData[] = sorted.map(([date, data], i) => {
    const dominant = Object.entries(data.cats).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'OTHER';
    return {
      date,
      total: data.total,
      color: catHex(dominant),
      barX: i * (barW + GAP),
    };
  });

  const maxTotal = Math.max(...days.map(d => d.total), 1);

  // Show month label if trip spans more than one month
  const months = [...new Set(days.map(d => d.date.slice(0, 7)))];
  const multiMonth = months.length > 1;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400">
          {t('expenses.spendingByDay')}
        </p>
        <div className={`text-right transition-opacity duration-100 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
            <p className="font-mono text-[11px] text-slate-400 leading-none mb-1">
              {hovered ? format(new Date(hovered.date), 'EEE dd MMM', { locale: dateLocale }) : '\u00A0'}
            </p>
            <p className="font-mono font-bold text-base leading-none tabular-nums text-slate-900 dark:text-slate-100">
              <span className="text-[9px] font-normal text-slate-400 mr-0.5">{currency}</span>
              {hovered ? hovered.total.toFixed(2) : '0.00'}
            </p>
          </div>
      </div>

      <div ref={containerRef}>
        <svg
          width={containerW}
          height={SVG_H}
          style={{ display: 'block' }}
        >
          {/* Baseline */}
          <line
            x1={0} y1={H_MAX} x2={containerW} y2={H_MAX}
            stroke="#e2e8f0"
            strokeWidth={0.5}
          />

          {days.map(day => {
            const barH = Math.max((day.total / maxTotal) * (H_MAX - 2), 2);
            const y = H_MAX - barH;
            const isHovered  = hovered?.date === day.date;
            const isSelected = selectedDay === day.date;
            // A selected day keeps its colour and everything else steps back,
            // so the chart says which day the ledger below is showing.
            const dimmed = (hovered && !isHovered) || (selectedDay && !isSelected);
            const labelText = format(new Date(day.date), 'd');
            const showMonth = multiMonth && day.date.endsWith('-01');

            return (
              <g
                key={day.date}
                onMouseEnter={() => setHovered(day)}
                onMouseLeave={() => setHovered(null)}
                onClick={onSelectDay ? () => onSelectDay(day.date) : undefined}
                role={onSelectDay ? 'button' : undefined}
                aria-pressed={onSelectDay ? isSelected : undefined}
                aria-label={onSelectDay ? format(new Date(day.date), 'EEE dd MMM', { locale: dateLocale }) : undefined}
                style={{ cursor: onSelectDay ? 'pointer' : 'default' }}
              >
                {/* Hit area */}
                <rect x={day.barX} y={0} width={barW} height={SVG_H} fill="transparent" />

                {/* Bar */}
                <rect
                  x={day.barX}
                  y={y}
                  width={barW}
                  height={barH}
                  rx={Math.min(3, barW / 4)}
                  fill={day.color}
                  style={{
                    opacity: dimmed ? 0.2 : 1,
                    transition: 'opacity 100ms ease',
                  }}
                />

                {/* Day label */}
                <text
                  x={day.barX + barW / 2}
                  y={SVG_H - 4}
                  textAnchor="middle"
                  fontSize={11}
                  fontFamily="monospace"
                  fill={isHovered || isSelected ? '#64748b' : '#94a3b8'}
                  style={{ transition: 'fill 100ms ease' }}
                >
                  {showMonth ? format(new Date(day.date), 'MMM', { locale: dateLocale }) : labelText}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
